"use client";
import { usePricing } from "@/components/pricing/PricingProvider";

import { useEffect, useRef } from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import { toGAItem } from "@/utils/ga4-events";

import type { Car, FilterState } from "@/types/cars";

interface ViewItemListTrackerProps {
    cars: Car[];
    filters: FilterState;
    page: number;
    itemListName?: string;
}

/**
 * Tiny client island whose only job is firing the GA4 view_item_list event
 * once on mount. Renders nothing visible.
 */
export default function ViewItemListTracker({ cars, filters, page, itemListName }: ViewItemListTrackerProps) {
    const { getNoHagglePrice } = usePricing();
    const firedRef = useRef<string | null>(null);

    useEffect(() => {
        const eventKey = JSON.stringify([itemListName || getItemListName(filters), page, filters.sort, cars.map(car => car.vin)]);
        if (firedRef.current === eventKey) return;
        firedRef.current = eventKey;

        try {
            const items = (cars || []).slice(0, 30).map((c: Car) =>
                toGAItem({
                    vin: c.vin,
                    name: `${c.year || ""} ${c.make || ""} ${c.model || ""}`.trim(),
                    price: getNoHagglePrice(c.price) ?? c.price,
                    make: c.make,
                    model: c.model,
                    year: c.year
                })
            );
            if (items.length > 0) {
                sendGTMEvent({
                    event: "view_item_list",
                    item_list_name: itemListName || getItemListName(filters),
                    items,
                    list_page: page,
                    list_sort: filters.sort || "specials"
                });
            }
        } catch (e) {
            console.warn("ViewItemListTracker: failed to send view_item_list", e);
        }
    }, [cars, filters, page, getNoHagglePrice, itemListName]);

    return null;
}

function getItemListName(filters: FilterState): string {
    const parts: string[] = [];
    if (filters.q && filters.q.trim()) {
        return `search_results: ${filters.q.trim().slice(0, 60)}`;
    }
    if (filters.make?.length) parts.push(filters.make.slice(0, 2).join(", "));
    if (filters.model?.length) parts.push(filters.model.slice(0, 2).join(", "));
    if (filters.bodystyle?.length) parts.push(filters.bodystyle.slice(0, 2).join(", "));
    if (filters.year) parts.push(`${filters.year[0]}-${filters.year[1]}`);
    if (filters.price) parts.push(`$${filters.price[0]}-$${filters.price[1]}`);
    const name = parts.join(" · ");
    return name ? `search_results: ${name}` : "search_results";
}
